import { Columns03 } from '@timelyapp/icons/solid-icons';
import { Box, Button, CustomIcon, Dropdown, DropdownMenu, Text, Timeline } from '@timelyapp/react';
import { DateTime } from 'luxon';
import React, { Suspense } from 'react';
import { useSelector } from 'react-redux';
import { getTicMemoriesUIState } from 'redux/modules/tic/tic.memories.ui-status.selectors';
import { getTicMemoryViewmode } from 'redux/selectors/user';
import TicAiPreferencesModal from '~domains/ai/preferences/components/Ai.Preferences.Modal';
import useMemoryDisplayOptions from '../hooks/useMemoryDisplayOptions';
import styles from './TicMemoryHeader.module.scss';
import { TimeTrackingHelpText } from './TicMemoryTimelineHoverOverInfo';

const LazyTicGlobalPreferencesDropdown = React.lazy(
  () => import('../../../calendar/tic/TicGlobalPreferencesDropdown')
);

type OpenMenu = 'general' | 'timeline' | 'configureAI' | 'column';

interface TicMemoryHeaderProps {
  date?: string;
}

export const TicMemoryHeader: React.FC<TicMemoryHeaderProps> = ({ date }) => {
  const [openMenu, setOpenMenu] = React.useState<null | OpenMenu>(null);
  const { dropdownItems, dropdownWidthPx } = useMemoryDisplayOptions({
    displayContext: 'in_global_memory_header',
  });
  const viewMode = useSelector(getTicMemoryViewmode);
  const { currentDate } = useSelector(getTicMemoriesUIState);
  // Use the passed date prop first, then the currentDate from the state
  const displayDate = date || currentDate;

  const getFormattedDate = () => {
    const draftDate = DateTime.fromISO(displayDate);
    const now = DateTime.now();
    const isToday = +draftDate.startOf('day') === +now.startOf('day');
    const shouldAppendYear = !draftDate.hasSame(now, 'year');
    const format = `${isToday ? `'Today'` : 'EEEE'}, ${
      shouldAppendYear ? 'MMM d, yyyy' : 'MMMM d'
    }`;
    return draftDate.toFormat(format);
  };

  return (
    <>
      {openMenu === 'configureAI' && <TicAiPreferencesModal onClose={() => setOpenMenu(null)} />}
      <Box className={styles.headerContainer}>
        <Dropdown
          onOpenChange={isOpen => setOpenMenu(isOpen ? 'general' : null)}
          open={openMenu === 'general'}
          trigger={
            <Timeline.Lane.Header
              active={openMenu === 'general'}
              data-testid="timeline-header-memory"
              variant="subtle"
              interactive
              gap="small"
            >
              <CustomIcon.Brain />
              <Box.Row gap={4}>
                <Text.Headline size="xsmall">Memory</Text.Headline>
                <Text.Label color="subtle" style={{ fontSize: '8px' }}>
                  -
                </Text.Label>
                <Text.Label size="xsmall" bold color="subtle">
                  100% private
                </Text.Label>
              </Box.Row>
            </Timeline.Lane.Header>
          }
        >
          <TimeTrackingHelpText.Privacy />
        </Dropdown>
        {displayDate && (
          <Timeline.Lane.Header variant="subtle">
            <Text.Label
              size="xsmall"
              color="subtle"
              data-testid="memory-timeline-date"
              className={styles.dateDisplay}
            >
              {getFormattedDate()}
            </Text.Label>
          </Timeline.Lane.Header>
        )}

        {/* Spacer */}
        <Timeline.Lane.Header variant="subtle" style={{ flexGrow: 1 }}>
          <Box.Spacer />
          {viewMode === 'timeline' && (
            <>
              <DropdownMenu
                returnFocusOnClose={false}
                width={dropdownWidthPx}
                onOpenChange={isOpen => {
                  const open = isOpen ? 'column' : null;
                  setOpenMenu(open);
                }}
                trigger={
                  <Button
                    priority="xlow"
                    size="xsmall"
                    isActive={openMenu === 'column'}
                    data-testid="timeline-lanes-button"
                  >
                    <Columns03 style={{ opacity: 0.5 }} />
                    Lanes
                  </Button>
                }
                align="end"
                side="bottom"
                sideOffset={4}
                // Not closing on choice makes for a less annoying experience when playing around with multiple settings.
                closeOnSelect={false}
                items={dropdownItems}
              />
            </>
          )}
          <Suspense>
            <LazyTicGlobalPreferencesDropdown />
          </Suspense>
        </Timeline.Lane.Header>
      </Box>
    </>
  );
};
